import React from 'react';
import { motion } from 'framer-motion';
import { Download, X, CheckCheck, Square } from 'lucide-react';
import styles from './AlbumBatchActionBar.module.css';

const GP_LOGO = 'https://d3ic3ju44zc444.cloudfront.net/assets/LogoImages/google-photos-6.svg';

function GooglePhotosLogo({ size = 14 }: { size?: number }) {
  return (
    <img
      src={GP_LOGO}
      width={size}
      height={size}
      alt="Google Photos"
      draggable={false}
      style={{ display: 'block', flexShrink: 0 }}
    />
  );
}

interface AlbumBatchActionBarProps {
  selectedCount: number;
  totalCount: number;
  onDownload: () => void;
  onSelectAll: () => void;
  onDeselectAll: () => void;
  /** True while "Select all" is loading not-yet-fetched pages. */
  selectingAll?: boolean;
  onExit: () => void;
  downloading: boolean;
  /** When provided, shows a "Sync to Google Photos" action in the bar */
  onSyncToGooglePhotos?: () => void;
  syncingToGooglePhotos?: boolean;
}

export function AlbumBatchActionBar({
  selectedCount,
  totalCount,
  onDownload,
  onSelectAll,
  onDeselectAll,
  selectingAll = false,
  onExit,
  downloading,
  onSyncToGooglePhotos,
  syncingToGooglePhotos = false,
}: AlbumBatchActionBarProps) {
  // `>=` not `===`: "Select all" can select more ids than are currently loaded
  // (totalCount only counts loaded photos), so the toggle must still read as all.
  const allSelected = selectedCount > 0 && selectedCount >= totalCount;

  return (
    <motion.div
      className={styles['bar']}
      initial={{ x: '-50%', y: 96, scale: 0.96 }}
      animate={{ x: '-50%', y: 0, scale: 1 }}
      exit={{ x: '-50%', y: 96, scale: 0.96 }}
      transition={{ type: 'spring', stiffness: 380, damping: 32 }}
    >
      <div className={styles['inner']}>
        {/* Select all / deselect all */}
        <motion.button
          onClick={allSelected ? onDeselectAll : onSelectAll}
          disabled={selectingAll}
          className={[styles['btn'], styles['btn-select']].join(' ')}
          whileTap={{ scale: 0.95 }}
          transition={{ type: 'spring', stiffness: 500, damping: 25 }}
          aria-label={allSelected ? 'Deselect all photos' : 'Select all photos'}
        >
          {allSelected
            ? <CheckCheck style={{ width: 15, height: 15, flexShrink: 0 }} />
            : <Square style={{ width: 15, height: 15, flexShrink: 0 }} />
          }
          <motion.span
            key={selectingAll ? 'loading' : selectedCount}
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.15 }}
          >
            {selectingAll ? 'Loading all…' : `${selectedCount} selected`}
          </motion.span>
        </motion.button>

        <div className={styles['divider']} />

        {/* Download */}
        <motion.button
          onClick={onDownload}
          disabled={selectedCount === 0 || downloading}
          className={[styles['btn'], styles['btn-export']].join(' ')}
          whileTap={{ scale: 0.95 }}
          transition={{ type: 'spring', stiffness: 500, damping: 25 }}
          aria-label={`Download ${selectedCount} selected photo${selectedCount !== 1 ? 's' : ''}`}
        >
          <Download style={{ width: 14, height: 14, flexShrink: 0 }} />
          {downloading
            ? 'Downloading...'
            : selectedCount > 1
              ? `Download ${selectedCount}`
              : 'Download'
          }
        </motion.button>

        {/* Google Photos sync — only shown when in GP-select mode */}
        {onSyncToGooglePhotos && (
          <>
            <div className={styles['divider']} />
            <motion.button
              onClick={onSyncToGooglePhotos}
              disabled={selectedCount === 0 || syncingToGooglePhotos}
              className={[styles['btn'], styles['btn-gpsync']].join(' ')}
              whileTap={{ scale: 0.95 }}
              transition={{ type: 'spring', stiffness: 500, damping: 25 }}
              aria-label={`Sync ${selectedCount} photos to Google Photos`}
            >
              <GooglePhotosLogo size={14} />
              {syncingToGooglePhotos
                ? 'Starting sync…'
                : selectedCount > 0
                  ? <>Sync {selectedCount} <span className={styles['hideOnMobile']}>to Google Photos</span></>
                  : <>Sync <span className={styles['hideOnMobile']}>to Google Photos</span></>
              }
            </motion.button>
          </>
        )}

        <div className={styles['divider']} />

        {/* Exit */}
        <motion.button
          onClick={onExit}
          className={[styles['btn'], styles['btn-exit']].join(' ')}
          whileTap={{ scale: 0.9 }}
          title="Exit selection"
          aria-label="Exit selection mode"
        >
          <X style={{ width: 15, height: 15 }} />
        </motion.button>
      </div>
    </motion.div>
  );
}
